import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Button,
  Stack,
  CircularProgress,
  Grid,
  TextField,
  InputAdornment,
  Menu,
  Pagination,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  IconButton,
  Divider
} from '@mui/material';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

// Hooks & Components
import {
  useTireWorkorderSearch,
  deleteTireWorkorder,
  updateTireWorkorderStatus,
  exportTireWorkorderSearch
} from '../../../../stores/tireWorkorderStore';
import { useNotifier } from '../../../../components/Notifier';
import { useCompanyList } from '../../../../stores/companyStore';
import DetailWorkorder from './DetailWorkorder';
import TabTireRepair from './TabTireRepair';
import { useLanguage } from '../../../../utils/i18n';

// Export Libs
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Papa from 'papaparse';
import ExcelJS from 'exceljs';

const TABS = ['All', 'Open', 'On Progress', 'Complete', 'Cancel'];

const statusFilterMap = {
  All: '',
  Open: 'open',
  'On Progress': 'on-progress',
  Complete: 'completed',
  Cancel: 'cancel'
};

const getStatusActionColor = (status) => {
  switch (status) {
    case 'on-progress':
      return 'warning';
    case 'completed':
      return 'success';
    case 'cancel':
      return 'error';
    default:
      return 'primary';
  }
};

const getStatusActionIcon = (status) => {
  switch (status) {
    case 'on-progress':
      return <PlayCircleOutlineIcon fontSize="small" />;
    case 'completed':
      return <CheckCircleOutlineIcon fontSize="small" />;
    case 'cancel':
      return <CancelOutlinedIcon fontSize="small" />;
    default:
      return <EditIcon fontSize="small" />;
  }
};

const getAllowedTransitions = (currentStatus) => {
  switch (currentStatus) {
    case 'open':
      return ['on-progress', 'cancel'];
    case 'on-progress':
      return ['completed', 'cancel'];
    default:
      return [];
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'open':
      return 'info';
    case 'on-progress':
      return 'warning';
    case 'completed':
      return 'success';
    case 'cancel':
      return 'error';
    default:
      return 'default';
  }
};
const formatRupiah = (value) => {
  if (!value) return '0';
  return new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(value);
};

export default function TabWorkOrder({ onUpdateTotalCount, refreshTrigger }) {
  const { t } = useLanguage();
  const [activeTabValue, setActiveTabValue] = useState(0);
  const [searchColumn, setSearchColumn] = useState('work_order_number');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const notify = useNotifier();
  const SEARCH_OPTIONS = [
    { value: 'work_order_number', label: t('tireRepairScrap.woInvoiceNo', 'Work Order No') },
    { value: 'serial_number', label: t('tyreItem.serialNumber', 'Serial Number') },
    { value: 'license_plate', label: t('setDriver.licensePlate', 'License Plate') },
    { value: 'last_position', label: t('detailTireInventory.lastPosition', 'Last Position') },
    { value: 'repair_type', label: t('tireCardHistory.repairType', 'Repair Type') },
    { value: 'cost_repair', label: t('tirePerformance.totalPrice', 'Total Price') },
    { value: 'vendor_name', label: t('masterVehicle.vendor', 'Vendor Name') },
    { value: 'tire_damage_name', label: t('menu.tire-damage', 'Tire Damage') }
  ];
  const statusLabels = (t) => ({
    open: t('tireRepairScrap.open', 'Open'),
    'on-progress': t('tireRepairScrap.onProgress', 'On Progress'),
    completed: t('tireRepairScrap.completed', 'Completed'),
    cancel: t('tireRepairScrap.canceled', 'Canceled')
  });

  const getStatusActionLabel = (status, t) => {
    switch (status) {
      case 'on-progress':
        return t('tireRepairScrap.startRepair', 'Start Repair');
      case 'completed':
        return t('tireRepairScrap.completeRepair', 'Complete Repair');
      case 'cancel':
        return t('tireRepairScrap.cancelRepair', 'Cancel Repair');
      default:
        return `${t('tireRepairScrap.markAs', 'Mark as')} ${statusLabels(t)[status] || status}`;
    }
  };

  const getTabLabel = (status, t) => {
    switch (status) {
      case 'All':
        return t('tireRepairScrap.all', t('general.all', 'All'));
      case 'Open':
        return t('tireRepairScrap.open', 'Open');
      case 'On Progress':
        return t('tireRepairScrap.onProgress', 'On Progress');
      case 'Complete':
        return t('tireRepairScrap.completed', 'Complete');
      case 'Cancel':
        return t('tireRepairScrap.canceled', t('general.cancel', 'Cancel'));
      default:
        return status;
    }
  };

  // Company Filter State
  const [selectedCompanyValues, setSelectedCompanyValues] = useState(() => {
    try {
      const rawUser = localStorage.getItem('user');
      if (rawUser) {
        const parsed = JSON.parse(rawUser);
        if (parsed && (parsed.company || parsed.company_id)) {
          return [String(parsed.company || parsed.company_id)];
        }
      }
      const rawCompany = localStorage.getItem('companyId');
      if (rawCompany) return [String(rawCompany)];
    } catch (e) {
      console.log(e);
    }
    return [];
  });
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const { companyOptions = [] } = useCompanyList();
  const filteredCompanyOptions = (companyOptions || []).filter((opt) => {
    if (!companySearchTerm) return true;
    const label = String(opt.label ?? opt.name ?? opt.value).toLowerCase();
    return label.includes(companySearchTerm.toLowerCase());
  });

  const handleCompanyChange = (e) => {
    const val = e.target.value;
    if (val.includes('all')) {
      if (selectedCompanyValues.length === (companyOptions || []).length) {
        setSelectedCompanyValues([]);
      } else {
        setSelectedCompanyValues((companyOptions || []).map((o) => String(o.value)));
      }
    } else {
      setSelectedCompanyValues(typeof val === 'string' ? val.split(',') : val);
    }
    setPage(1);
  };

  const renderCompanyValue = (selected) => {
    if (selected.length === 0) return t('tireRepairScrap.selectCompany', 'Select Company');
    if (companyOptions && selected.length === companyOptions.length) return t('tireRepairScrap.allCompany', 'All Company');
    const selectedObjs = (companyOptions || [])
      .filter((o) => selected.includes(String(o.value)))
      .map((o) => o.label ?? o.name ?? String(o.value));
    if (selectedObjs.length > 2) return `${selectedObjs.length} ${t('tireRepairScrap.companySelected', 'Company Selected')}`;
    return selectedObjs.join(', ');
  };

  // Date Filter State
  const [dateFilter, setDateFilter] = useState('Last Week');
  const [customDate, setCustomDate] = useState({
    startDate: dayjs(),
    endDate: dayjs()
  });

  const getDateRange = () => {
    let startDate, endDate;
    switch (dateFilter) {
      case 'To Day':
        startDate = dayjs().format('YYYY-MM-DD');
        endDate = dayjs().format('YYYY-MM-DD');
        break;
      case 'Last Week':
        startDate = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        endDate = dayjs().format('YYYY-MM-DD');
        break;
      case 'Last Month':
        startDate = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
        endDate = dayjs().format('YYYY-MM-DD');
        break;
      case 'Custom':
        startDate = customDate.startDate.format('YYYY-MM-DD');
        endDate = customDate.endDate.format('YYYY-MM-DD');
        break;
      default:
        startDate = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        endDate = dayjs().format('YYYY-MM-DD');
        break;
    }
    return { startDate, endDate };
  };

  const { startDate, endDate } = getDateRange();

  const commonExtraFilters = [];
  if (startDate && endDate) {
    commonExtraFilters.push({
      field: 'repair_date',
      operator: 'range',
      value: startDate,
      value1: endDate
    });
  }

  const activeTabStatus = statusFilterMap[TABS[activeTabValue]];
  const companiesArg = selectedCompanyValues.map((val) => ({ value: val }));

  // Fetch Main List Data
  const { data, totalRecord, isLoading, refetch } = useTireWorkorderSearch(
    page - 1,
    rowsPerPage,
    companiesArg,
    searchQuery ? searchColumn : null,
    searchQuery,
    activeTabStatus ? [...commonExtraFilters, { field: 'status', value: activeTabStatus }] : commonExtraFilters
  );

  // Fetch Counts for Badges
  const commonArgs = [0, 1, companiesArg, searchQuery ? searchColumn : null, searchQuery];

  const { totalRecord: countAll, refetch: refetchAllCount } = useTireWorkorderSearch(...commonArgs, commonExtraFilters);
  const { totalRecord: countOpen, refetch: refetchOpen } = useTireWorkorderSearch(...commonArgs, [
    ...commonExtraFilters,
    { field: 'status', value: 'open' }
  ]);
  const { totalRecord: countProg, refetch: refetchProg } = useTireWorkorderSearch(...commonArgs, [
    ...commonExtraFilters,
    { field: 'status', value: 'on-progress' }
  ]);
  const { totalRecord: countComp, refetch: refetchComp } = useTireWorkorderSearch(...commonArgs, [
    ...commonExtraFilters,
    { field: 'status', value: 'completed' }
  ]);
  const { totalRecord: countCanc, refetch: refetchCanc } = useTireWorkorderSearch(...commonArgs, [
    ...commonExtraFilters,
    { field: 'status', value: 'cancel' }
  ]);

  const getTabCount = (idx) => {
    switch (idx) {
      case 0:
        return countAll || 0;
      case 1:
        return countOpen || 0;
      case 2:
        return countProg || 0;
      case 3:
        return countComp || 0;
      case 4:
        return countCanc || 0;
      default:
        return 0;
    }
  };

  const activeCountTotal = (countOpen || 0) + (countProg || 0);

  useEffect(() => {
    if (onUpdateTotalCount) {
      onUpdateTotalCount(activeCountTotal);
    }
  }, [activeCountTotal, onUpdateTotalCount]);

  useEffect(() => {
    if (refreshTrigger > 0) {
      refetchAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  const refetchAll = () => {
    if (refetch) refetch();
    if (refetchAllCount) refetchAllCount();
    if (refetchOpen) refetchOpen();
    if (refetchProg) refetchProg();
    if (refetchComp) refetchComp();
    if (refetchCanc) refetchCanc();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTabValue(newValue);
    setPage(1);
    setSelectedWOs([]);
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      setSearchQuery(searchInput);
      setPage(1);
    }
  };

  // Modals state
  const [detailModal, setDetailModal] = useState({ open: false, data: null });
  const [editModal, setEditModal] = useState({ open: false, data: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
  const [statusConfirm, setStatusConfirm] = useState({ open: false, data: null, newStatus: '', notes: null, completeDate: null });

  // Delete
  const handleDelete = async () => {
    try {
      await deleteTireWorkorder(deleteConfirm.id);
      notify({ message: t('tireRepairScrap.workOrderDeletedSuccess', 'Work Order deleted successfully'), severity: 'success' });
      setDeleteConfirm({ open: false, id: null });
      refetchAll();
      if (detailModal.open && detailModal.data?.id === deleteConfirm.id) {
        setDetailModal({ open: false, data: null });
      }
    } catch {
      notify({ message: t('tireRepairScrap.failedToDeleteWorkOrder', 'Failed to delete Work Order'), severity: 'error' });
    }
  };

  // Status Change
  const handleStatusChange = async () => {
    try {
      await updateTireWorkorderStatus(statusConfirm.data.id, statusConfirm.newStatus, statusConfirm.notes, statusConfirm.completeDate);
      notify({
        message: `${t('tireRepairScrap.statusUpdatedTo', 'Status updated to')} ${statusLabels(t)[statusConfirm.newStatus]}`,
        severity: 'success'
      });
      setStatusConfirm({ open: false, data: null, newStatus: '', notes: null, completeDate: null });
      setDetailModal({ open: false, data: null });
      refetchAll();
    } catch {
      notify({ message: t('tireRepairScrap.failedToUpdateStatus', 'Failed to update status'), severity: 'error' });
    }
  };

  // Bulk Status Update
  const [selectedWOs, setSelectedWOs] = useState([]);
  const [bulkStatusConfirm, setBulkStatusConfirm] = useState({ open: false, newStatus: '' });
  const [bulkNotes, setBulkNotes] = useState({});
  const [globalNote, setGlobalNote] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [globalCompleteDate, setGlobalCompleteDate] = useState(dayjs());
  const [globalMinRepairDate, setGlobalMinRepairDate] = useState(null);

  useEffect(() => {
    if (bulkStatusConfirm.open && bulkStatusConfirm.newStatus === 'completed' && selectedWOs.length > 0) {
      let maxDate = null;
      data
        ?.filter((d) => selectedWOs.includes(d.id))
        .forEach((wo) => {
          if (wo.repair_date) {
            const rDate = dayjs(wo.repair_date);
            if (!maxDate || rDate.isAfter(maxDate)) {
              maxDate = rDate;
            }
          }
        });
      setGlobalMinRepairDate(maxDate);
      if (maxDate && dayjs().isBefore(maxDate, 'day')) {
        setGlobalCompleteDate(maxDate);
      } else {
        setGlobalCompleteDate(dayjs());
      }
    } else {
      setGlobalCompleteDate(dayjs());
      setGlobalMinRepairDate(null);
    }
  }, [bulkStatusConfirm.open, bulkStatusConfirm.newStatus, selectedWOs, data]);

  const handleSelectWo = (e, id) => {
    if (e.target.checked) {
      setSelectedWOs((prev) => [...prev, id]);
    } else {
      setSelectedWOs((prev) => prev.filter((item) => item !== id));
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const ids = data.map((wo) => wo.id);
      setSelectedWOs(ids);
    } else {
      setSelectedWOs([]);
    }
  };

  const handleBulkUpdate = async () => {
    if (!bulkStatusConfirm.newStatus) {
      notify({ message: t('tireRepairScrap.pleaseSelectStatusToUpdate', 'Please select a status to update'), severity: 'warning' });
      return;
    }
    if (bulkStatusConfirm.newStatus === 'completed' && !globalCompleteDate) {
      notify({ message: t('tireRepairScrap.pleaseEnterCompleteDate', 'Please enter Complete Date'), severity: 'warning' });
      return;
    }

    setBulkLoading(true);
    try {
      const promises = selectedWOs.map((id) => {
        const notes = bulkStatusConfirm.newStatus === 'cancel' ? bulkNotes[id] : null;
        const completeDateFormatted = bulkStatusConfirm.newStatus === 'completed' ? globalCompleteDate.format('YYYY-MM-DD') : null;
        return updateTireWorkorderStatus(id, bulkStatusConfirm.newStatus, notes, completeDateFormatted);
      });
      await Promise.all(promises);
      notify({
        message: `${t('tireRepairScrap.successfullyUpdated', 'Successfully updated')} ${selectedWOs.length} ${t('tireRepairScrap.workOrdersTo', 'Work Orders to')} ${statusLabels(t)[bulkStatusConfirm.newStatus]}`,
        severity: 'success'
      });
      setSelectedWOs([]);
      setBulkStatusConfirm({ open: false, newStatus: '' });
      refetchAll();
    } catch {
      notify({ message: t('tireRepairScrap.failedToUpdateSomeWorkOrders', 'Failed to update some Work Orders'), severity: 'error' });
    } finally {
      setBulkLoading(false);
    }
  };

  // Action Menu
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuWo, setActionMenuWo] = useState(null);

  const handleMenuClick = (e, wo) => {
    e.stopPropagation();
    setActionMenuAnchor(e.currentTarget);
    setActionMenuWo(wo);
  };
  const handleMenuClose = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setActionMenuAnchor(null);
    setActionMenuWo(null);
  };

  // Export Logic
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const handleExportClick = (event) => setExportAnchorEl(event.currentTarget);
  const handleExportClose = () => setExportAnchorEl(null);

  const formatExportData = (exportData) => {
    return exportData.map((row) => ({
      'WO Number': row.work_order_number_internal || row.work_order_number || '-',
      'Serial Number': row.serial_number || '-',
      Status: statusLabels(t)[row.status] || row.status,
      'License Plate': row.join?.vehicle_license_plate || row.license_plate || row.last_vehicle || '-',
      Merk: row.join?.vehicle_merk || '-',
      Model: row.join?.vehicle_model || '-',
      Type: row.vehicle_type_name || row.join?.vehicle_type_name || '-',
      Name: row.join?.vehicle_name || '-',
      Year: row.join?.vehicle_year || '-',
      'Last Position': row.last_position || '-',
      'Tire Brand': row.tire_brand_name || '-',
      'Tire Size': row.tire_size_code || '-',
      'Tire Spec': row.tire_spec_name || '-',
      Casing: `${t('tireRepairScrap.retread', 'Retread')} ${row.count_retread || 0}/3`,
      'Repair Type': row.repair_type || '-',
      'Tire Damage': row.tire_damage_name || '-',
      'Handled By': row.vendor_name || '-',
      'Mechanic / PIC': row.mechanic_name || '-',
      'Activity Date': row.repair_date ? dayjs(row.repair_date).format('DD-MM-YYYY') : '-',
      'Est Complete Date': row.estimated_complete_date ? dayjs(row.estimated_complete_date).format('DD-MM-YYYY') : '-',
      'WO / Invoice No.': row.work_order_number || '-',
      'Repair Cost': row.cost_repair || 0
    }));
  };

  const fetchAllForExport = async () => {
    return await exportTireWorkorderSearch(
      companiesArg,
      searchQuery ? searchColumn : null,
      searchQuery,
      activeTabStatus ? [...commonExtraFilters, { field: 'status', value: activeTabStatus }] : commonExtraFilters
    );
  };

  const exportToCSV = async () => {
    handleExportClose();
    const dataToExport = await fetchAllForExport();
    const formattedData = formatExportData(dataToExport || []);
    const csvData = Papa.unparse(formattedData);
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `work-orders-${dayjs().format('YYYYMMDD')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = async () => {
    handleExportClose();
    const dataToExport = await fetchAllForExport();
    const formattedData = formatExportData(dataToExport || []);
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Work Orders');

    if (formattedData.length > 0) {
      const headers = Object.keys(formattedData[0]);
      worksheet.columns = headers.map((h) => ({ header: h, key: h, width: 20 }));
      formattedData.forEach((row) => worksheet.addRow(row));

      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF1976D2' }
        };
      });
    }
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `work-orders-${dayjs().format('YYYYMMDD')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = async () => {
    handleExportClose();
    const dataToExport = await fetchAllForExport();
    const formattedData = formatExportData(dataToExport || []);
    const doc = new jsPDF('landscape');
    if (formattedData.length > 0) {
      const headers = Object.keys(formattedData[0]);
      const rows = formattedData.map((r) => Object.values(r));
      doc.text(t('tireRepairScrap.workOrderReport', 'Work Orders Report'), 14, 15);
      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: 20,
        headStyles: { fillColor: [25, 118, 210] }
      });
    }
    doc.save(`work-orders-${dayjs().format('YYYYMMDD')}.pdf`);
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <Select
          multiple
          size="small"
          value={selectedCompanyValues}
          onChange={handleCompanyChange}
          renderValue={renderCompanyValue}
          sx={{ minWidth: { xs: '100%', md: 200 }, backgroundColor: 'white' }}
          displayEmpty
          MenuProps={{
            autoFocus: false,
            PaperProps: { style: { maxHeight: 300 } }
          }}
        >
          <Box sx={{ p: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder={t('tireRepairScrap.searchCompany', 'Search Company...')}
              value={companySearchTerm}
              onChange={(e) => setCompanySearchTerm(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </Box>
          <MenuItem value="all">
            <Checkbox checked={selectedCompanyValues.length === companyOptions?.length && companyOptions?.length > 0} />
            <Typography variant="body2">{t('tireRepairScrap.selectAll', 'Select All')}</Typography>
          </MenuItem>
          <Divider />
          {filteredCompanyOptions.map((opt) => (
            <MenuItem key={opt.value} value={String(opt.value)}>
              <Checkbox checked={selectedCompanyValues.includes(String(opt.value))} />
              <Typography variant="body2">{opt.label ?? opt.name}</Typography>
            </MenuItem>
          ))}
        </Select>

        <Select
          size="small"
          value={dateFilter}
          onChange={(e) => {
            setDateFilter(e.target.value);
            setPage(1);
          }}
          sx={{ minWidth: { xs: '100%', md: 150 }, backgroundColor: 'white' }}
        >
          {[
            { value: 'To Day', label: t('tireRepairScrap.today', 'To Day') },
            { value: 'Last Week', label: t('tireRepairScrap.lastWeek', 'Last Week') },
            { value: 'Last Month', label: t('tireRepairScrap.lastMonth', 'Last Month') },
            { value: 'Custom', label: t('tireRepairScrap.custom', 'Custom') }
          ].map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>

        {dateFilter === 'Custom' && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" gap={1} alignItems="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
              <DatePicker
                label={t('tireRepairScrap.from', 'From')}
                value={customDate.startDate}
                onChange={(newValue) => {
                  setCustomDate({ ...customDate, startDate: newValue });
                  setPage(1);
                }}
                slotProps={{ textField: { size: 'small', sx: { width: '100%', md: 140, backgroundColor: 'white' } } }}
                format="DD-MM-YYYY"
              />
              <DatePicker
                label={t('tireRepairScrap.to', 'To')}
                value={customDate.endDate}
                onChange={(newValue) => {
                  setCustomDate({ ...customDate, endDate: newValue });
                  setPage(1);
                }}
                minDate={customDate.startDate}
                maxDate={customDate.startDate.add(1, 'month')}
                slotProps={{ textField: { size: 'small', sx: { width: '100%', md: 140, backgroundColor: 'white' } } }}
                format="DD-MM-YYYY"
              />
            </Box>
          </LocalizationProvider>
        )}

        <TextField
          select
          size="small"
          value={searchColumn}
          onChange={(e) => {
            setSearchColumn(e.target.value);
            setSearchInput('');
            setSearchQuery('');
            setPage(1);
          }}
          sx={{ width: { xs: '100%', md: 160 }, backgroundColor: 'white', borderRadius: 1, ml: { md: 'auto' } }}
        >
          {SEARCH_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          placeholder={`${t('tireRepairScrap.search', 'Search')} ${SEARCH_OPTIONS.find((o) => o.value === searchColumn)?.label || ''}...`}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ width: { xs: '100%', md: 250 }, backgroundColor: 'white', borderRadius: 1 }}
        />
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExportClick}
          sx={{ backgroundColor: 'white', textTransform: 'none' }}
          size="medium"
        >
          {t('tireRepairScrap.export', 'Export')}
        </Button>
        <Menu anchorEl={exportAnchorEl} open={Boolean(exportAnchorEl)} onClose={handleExportClose}>
          <MenuItem onClick={exportToCSV}>CSV</MenuItem>
          <MenuItem onClick={exportToExcel}>Excel</MenuItem>
          <MenuItem onClick={exportToPDF}>PDF</MenuItem>
        </Menu>
      </Box>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={{
          mb: 2,
          position: 'sticky',
          top: 48,
          zIndex: 10,
          bgcolor: '#f9fafb',
          py: 1,
          mt: -1
        }}
        spacing={2}
      >
        <Box
          sx={{
            width: '100%',
            overflowX: 'auto',
            p: 0.5,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: '1px solid #e2e8f0',
            display: 'inline-flex'
          }}
        >
          <Tabs
            value={activeTabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{ style: { display: 'none' } }}
            sx={{
              minHeight: 40,
              '& .MuiTab-root': {
                minHeight: 40,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                borderRadius: 1.5,
                color: '#64748b',
                mr: 1,
                transition: 'all 0.3s ease',
                '&:hover': { backgroundColor: '#f1f5f9' },
                '&.Mui-selected': { color: 'primary.main', backgroundColor: '#eff6ff', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }
              }
            }}
          >
            {TABS.map((status, idx) => (
              <Tab
                key={status}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    {getTabLabel(status, t)}
                    <Chip
                      label={getTabCount(idx)}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        backgroundColor: activeTabValue === idx ? 'primary.main' : '#e2e8f0',
                        color: activeTabValue === idx ? '#fff' : '#64748b'
                      }}
                    />
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>
      </Stack>

      {/* Card List Area */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" my={10}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {data && data.length > 0 && getAllowedTransitions(activeTabStatus).length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', pl: 1 }}>
                <Checkbox
                  checked={selectedWOs.length === data.length && data.length > 0}
                  indeterminate={selectedWOs.length > 0 && selectedWOs.length < data.length}
                  onChange={handleSelectAll}
                />
                <Typography variant="body2" color="textSecondary">
                  {t('tireRepairScrap.selectAll', 'Select All')}
                </Typography>
              </Box>

              {selectedWOs.length > 0 && (
                <Stack direction="row" spacing={1}>
                  {getAllowedTransitions(activeTabStatus).map((nextStatus) => (
                    <Button
                      key={nextStatus}
                      variant="contained"
                      color={getStatusActionColor(nextStatus)}
                      onClick={() => {
                        setBulkStatusConfirm({ open: true, newStatus: nextStatus });
                        setGlobalNote('');
                        const newNotes = {};
                        data
                          .filter((d) => selectedWOs.includes(d.id))
                          .forEach((wo) => {
                            newNotes[wo.id] = wo.notes || '';
                          });
                        setBulkNotes(newNotes);
                      }}
                      size="medium"
                      sx={{ textTransform: 'none' }}
                    >
                      {getStatusActionLabel(nextStatus, t)} ({selectedWOs.length})
                    </Button>
                  ))}
                </Stack>
              )}
            </Box>
          )}
          <Stack spacing={1.5}>
            {data && data.length > 0 ? (
              data.map((wo) => {
                const woNumber = wo.work_order_number_internal || wo.work_order_number || '-';
                return (
                  <Card
                    key={wo.id}
                    onClick={() => setDetailModal({ open: true, data: wo })}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      boxShadow: 'none',
                      transition: 'all 0.15s ease',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      '&:hover': { borderColor: 'primary.light', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }
                    }}
                  >
                    <CardContent sx={{ flex: 1, py: { xs: 1, md: 1.5 }, px: 2, '&:last-child': { pb: { xs: 1, md: 1.5 } } }}>
                      {(wo.status === 'open' || wo.status === 'on-progress') && (
                        <Box sx={{ position: 'absolute', top: 8, right: 8 }} onClick={(e) => e.stopPropagation()}>
                          <IconButton size="small" onClick={(e) => handleMenuClick(e, wo)}>
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                      )}
                      <Grid container spacing={2} alignItems="center">
                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                          <Box display="flex" alignItems="flex-start">
                            {getAllowedTransitions(activeTabStatus).length > 0 && (
                              <Box onClick={(e) => e.stopPropagation()} sx={{ mr: 1, mt: -0.5, ml: -1 }}>
                                <Checkbox checked={selectedWOs.includes(wo.id)} onChange={(e) => handleSelectWo(e, wo.id)} />
                              </Box>
                            )}
                            <Box>
                              <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                                <Typography variant="subtitle1" fontWeight="800" sx={{ lineHeight: 1 }}>
                                  {woNumber}
                                </Typography>
                                <Chip
                                  label={statusLabels(t)[wo.status] || wo.status}
                                  size="small"
                                  color={getStatusColor(wo.status)}
                                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold' }}
                                />
                              </Stack>
                              <Typography variant="caption" color="textSecondary" display="block">
                                {t('tireRepairScrap.sn', 'SN:')} {wo.serial_number || '-'}
                              </Typography>
                              <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <LocalShippingIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {wo.join.vehicle_license_plate || wo.license_plate || '-'}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="textSecondary" display="block">
                                  {wo.tire_brand_name || wo.tire_brand || '-'} • {wo.tire_size_code || wo.tire_size || '-'}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Grid>

                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                          <Typography variant="caption" color="textSecondary">
                            {t('tireCardHistory.repairType', 'Repair Type')} & {t('tireRepairScrap.damage', 'Damage')}
                          </Typography>
                          <Typography variant="body2" fontWeight="600" color="primary.dark">
                            {wo.repair_type || '-'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" display="block">
                            {t('tireRepairScrap.damage', 'Damage:')}{' '}
                            <Box component="span" fontWeight="500">
                              {wo.tire_damage_name || '-'}
                            </Box>
                          </Typography>
                          <Typography variant="caption" color="textSecondary" display="block">
                            {t('tireRepairScrap.by', 'by')} {wo.mechanic_name || '-'}
                          </Typography>
                        </Grid>

                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                          <Typography variant="caption" color="textSecondary">
                            {t('tireRepairScrap.vendor', 'Vendor')}
                          </Typography>
                          <Typography variant="body2" noWrap>
                            {wo.vendor_name || '-'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {wo.vendor_type || '-'}
                          </Typography>
                        </Grid>

                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-end" gap={1}>
                            <Box flex={1}>
                              <Typography variant="caption" color="textSecondary" display="block" sx={{ lineHeight: 1.2 }}>
                                {t('tireRepairScrap.repairDate', 'Repair Date')}
                              </Typography>
                              <Typography variant="body2" color="primary.main" fontWeight="bold">
                                {wo.repair_date ? dayjs(wo.repair_date).format('DD/MM/YYYY') : '-'}
                              </Typography>
                            </Box>
                            <Box flex={1}>
                              <Typography variant="caption" color="textSecondary" display="block" sx={{ lineHeight: 1.2 }}>
                                {t('tireRepairScrap.estComplete', 'Est. Complete')}
                              </Typography>
                              <Typography variant="body2" color="warning.main" fontWeight="bold">
                                {wo.estimated_complete_date ? dayjs(wo.estimated_complete_date).format('DD/MM/YYYY') : '-'}
                              </Typography>
                            </Box>
                          </Box>
                          {wo.status === 'completed' && (
                            <Box
                              mt={0.5}
                              p={0.75}
                              sx={{
                                backgroundColor: '#f8fafc',
                                borderRadius: 1,
                                border: '1px solid #e2e8f0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 2,
                                minWidth: '100%',
                                alignItems: 'center'
                              }}
                            >
                              <Typography variant="caption" color="textSecondary" display="block" sx={{ lineHeight: 1.2 }}>
                                {t('tireRepairScrap.completedAt', 'Completed At')}
                              </Typography>
                              <Typography variant="body2" color="primary.main" fontWeight="bold">
                                {wo.completed_date ? dayjs(wo.completed_date).format('DD/MM/YYYY') : '-'}
                              </Typography>
                            </Box>
                          )}
                          <Box
                            mt={1}
                            p={0.75}
                            sx={{
                              backgroundColor: '#f8fafc',
                              borderRadius: 1,
                              border: '1px solid #e2e8f0',
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 2,
                              minWidth: '100%'
                            }}
                          >
                            <Typography variant="caption" color="textSecondary" display="block">
                              {t('tireRepairScrap.totalCost', 'Total Cost')}
                            </Typography>
                            <Typography variant="body2" color="primary.dark" fontWeight="800">
                              Rp {wo.cost_repair ? formatRupiah(wo.cost_repair) : '0'}
                            </Typography>
                          </Box>

                          {/* Individual Action Buttons Removed - Now in Menu */}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                );
              })
            ) : (
              <Box p={5} textAlign="center" backgroundColor="white" borderRadius={2} border="1px solid #eee">
                <Typography color="textSecondary">{t('tireRepairScrap.noDataAvailable', 'No data available.')}</Typography>
              </Box>
            )}
          </Stack>

          {/* Pagination */}
          {totalRecord > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('tireRepairScrap.rowsPerPage', 'Rows per page:')}
                </Typography>
                <Select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(e.target.value);
                    setPage(1);
                  }}
                  size="small"
                  sx={{ height: 30, '.MuiSelect-select': { py: 0.5 } }}
                >
                  {[10, 25, 50, 100].map((val) => (
                    <MenuItem key={val} value={val}>
                      {val}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  {`${(page - 1) * rowsPerPage + 1}-${Math.min(page * rowsPerPage, totalRecord)} ${t('tireRepairScrap.of', 'of')} ${totalRecord}`}
                </Typography>
              </Box>
              <Pagination
                count={Math.ceil(totalRecord / rowsPerPage)}
                page={page}
                onChange={(e, val) => setPage(val)}
                color="primary"
                shape="rounded"
              />
            </Box>
          )}
        </Box>
      )}

      {/* Action Menu */}
      <Menu anchorEl={actionMenuAnchor} open={Boolean(actionMenuAnchor)} onClose={handleMenuClose} onClick={(e) => e.stopPropagation()}>
        {(actionMenuWo?.status === 'open' || actionMenuWo?.status === 'on-progress') && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              setEditModal({ open: true, data: actionMenuWo });
              handleMenuClose();
            }}
          >
            <EditIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
            <Typography color="primary">{t('tireRepairScrap.edit', 'Edit')}</Typography>
          </MenuItem>
        )}
        {actionMenuWo?.status === 'open' && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              setDeleteConfirm({ open: true, id: actionMenuWo?.id });
              handleMenuClose();
            }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
            <Typography color="error">{t('tireRepairScrap.delete', 'Delete')}</Typography>
          </MenuItem>
        )}

        {actionMenuWo && getAllowedTransitions(actionMenuWo.status).length > 0 && <Divider sx={{ my: 0.5 }} />}
        {actionMenuWo &&
          getAllowedTransitions(actionMenuWo.status).map((nextStatus) => (
            <MenuItem
              key={nextStatus}
              onClick={(e) => {
                e.stopPropagation();
                setBulkStatusConfirm({ open: true, newStatus: nextStatus });
                setGlobalNote('');
                setBulkNotes({ [actionMenuWo.id]: actionMenuWo.notes || '' });
                setSelectedWOs([actionMenuWo.id]);
                handleMenuClose();
              }}
            >
              <Box sx={{ mr: 1, display: 'flex', color: `${getStatusActionColor(nextStatus)}.main` }}>
                {getStatusActionIcon(nextStatus)}
              </Box>
              <Typography color={`${getStatusActionColor(nextStatus)}.main`}>{getStatusActionLabel(nextStatus, t)}</Typography>
            </MenuItem>
          ))}
      </Menu>

      {/* Bulk Status Confirmation Dialog */}
      <Dialog open={bulkStatusConfirm.open} onClose={() => setBulkStatusConfirm({ open: false, newStatus: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('tireRepairScrap.confirm', 'Confirm')} {getStatusActionLabel(bulkStatusConfirm.newStatus, t)}
        </DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            {t('tireRepairScrap.areYouSurePerform', 'Are you sure you want to perform')}{' '}
            <strong>{getStatusActionLabel(bulkStatusConfirm.newStatus, t)}</strong> {t('tireRepairScrap.on', 'on')}{' '}
            <strong>{selectedWOs.length}</strong> {t('tireRepairScrap.selectedWorkOrderCount', 'selected Work Order(s)?')}
          </Typography>

          {bulkStatusConfirm.newStatus === 'cancel' && (
            <>
              <TextField
                fullWidth
                multiline
                rows={2}
                label={t('tireRepairScrap.globalCancellationReason', 'Global Cancellation Reason')}
                placeholder={t('tireRepairScrap.applyThisNote', 'Apply this note to all selected Work Orders in English (optional)')}
                value={globalNote}
                onChange={(e) => {
                  const val = e.target.value;
                  setGlobalNote(val);
                  const updatedNotes = { ...bulkNotes };
                  selectedWOs.forEach((id) => {
                    updatedNotes[id] = val;
                  });
                  setBulkNotes(updatedNotes);
                }}
                sx={{ mb: 2 }}
                helperText={t('tireRepairScrap.typingHereWillOverwrite', 'Typing here will overwrite all individual notes below.')}
              />
              <Typography variant="subtitle2" mb={1} color="textSecondary">
                {t('tireRepairScrap.selectedWorkOrder', 'Selected Work Order')}:
              </Typography>
              <Box sx={{ maxHeight: 250, overflowY: 'auto', p: 1.5, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                {data
                  ?.filter((d) => selectedWOs.includes(d.id))
                  .map((wo) => (
                    <Box key={wo.id} sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {wo.work_order_number_internal || wo.work_order_number || wo.serial_number}
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={t('tireRepairScrap.individualReason', 'Individual reason (optional)')}
                        value={bulkNotes[wo.id] || ''}
                        onChange={(e) => setBulkNotes({ ...bulkNotes, [wo.id]: e.target.value })}
                        sx={{ mt: 1, bgcolor: 'white' }}
                      />
                    </Box>
                  ))}
              </Box>
            </>
          )}

          {bulkStatusConfirm.newStatus === 'completed' && (
            <Box mt={2} mb={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={t('tireRepairScrap.completeDate', 'Complete Date *')}
                  format="DD/MM/YYYY"
                  minDate={globalMinRepairDate}
                  value={globalCompleteDate}
                  onChange={(newVal) => setGlobalCompleteDate(newVal)}
                  slotProps={{
                    textField: { fullWidth: true, size: 'small', required: true }
                  }}
                />
              </LocalizationProvider>
            </Box>
          )}

          {bulkStatusConfirm.newStatus !== 'cancel' && bulkStatusConfirm.newStatus !== '' && (
            <Box sx={{ maxHeight: 200, overflowY: 'auto', p: 1.5, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
              <Typography variant="subtitle2" mb={1} color="textSecondary">
                {t('tireRepairScrap.selectedWorkOrder', 'Selected Work Order')}:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {data
                  ?.filter((d) => selectedWOs.includes(d.id))
                  .map((wo) => (
                    <li key={wo.id}>
                      <Typography variant="body2">{wo.work_order_number_internal || wo.work_order_number || wo.serial_number}</Typography>
                    </li>
                  ))}
              </ul>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkStatusConfirm({ open: false, newStatus: '' })} color="inherit" disabled={bulkLoading}>
            {t('general.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleBulkUpdate} color="primary" variant="contained" disabled={!bulkStatusConfirm.newStatus || bulkLoading}>
            {bulkLoading ? <CircularProgress size={24} /> : t('general.update', 'Update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, id: null })}>
        <DialogTitle>{t('tireRepairScrap.deleteWorkOrder', 'Delete Work Order')}</DialogTitle>
        <DialogContent>
          {t('tireRepairScrap.areYouSureDeleteWo', 'Are you sure you want to delete this Work Order? This action cannot be undone.')}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, id: null })} color="inherit">
            {t('general.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('tireRepairScrap.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Confirmation Dialog */}
      <Dialog
        open={statusConfirm.open}
        onClose={() => setStatusConfirm({ open: false, data: null, newStatus: '', notes: null, completeDate: null })}
      >
        <DialogTitle>{t('tireRepairScrap.changeStatus', 'Change Status')}</DialogTitle>
        <DialogContent>
          {t('tireRepairScrap.areYouSureChangeStatus', 'Are you sure you want to change the status to')}{' '}
          <strong>{statusLabels(t)[statusConfirm.newStatus]}</strong>?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setStatusConfirm({ open: false, data: null, newStatus: '', notes: null, completeDate: null })}
            color="inherit"
          >
            {t('general.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleStatusChange} color="primary" variant="contained">
            {t('tireRepairScrap.confirm', 'Confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Modal */}
      <TabTireRepair
        open={editModal.open}
        onClose={() => setEditModal({ open: false, data: null })}
        editData={editModal.data}
        onSuccess={() => {
          setEditModal({ open: false, data: null });
          refetchAll();
        }}
      />

      {/* Detail Modal */}
      <DetailWorkorder
        open={detailModal.open}
        onClose={() => setDetailModal({ open: false, data: null })}
        data={detailModal.data}
        onUpdateStatus={(data, newStatus, notes, completeDate) => setStatusConfirm({ open: true, data, newStatus, notes, completeDate })}
        getAllowedTransitions={getAllowedTransitions}
      />
    </Box>
  );
}
